import { useCallback, useEffect, useState } from 'react';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setGroupList, setGroupName } from 'v3/data/store/group/actions';
import { IGroup, IGroupsList } from 'v3/data/store/group/types';
import { setAllGroups, setGroupAdminList } from 'v3/data/store/groupAdmin/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import * as api from 'v3/services/api';
import { getGroupList, updateGroupsName } from 'v3/services/api';
import { EditIcon } from 'v3/shared/components/MobileTable/styles';
import { GenericNameModal } from 'v3/shared/components/Modal/GenericNameModal';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { SubtitleMediumBoldStyled, SubtitleMediumStyled } from 'v3/shared/typography/SubtitleMedium/styles';
import { AlignStatusContainer, BoxCustom, Container, Content } from './styles';

interface GroupsTableProps {
  listItem: IGroupsList;
}

export const CreateGroupListMobile: React.FC<GroupsTableProps> = ({ listItem }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IGroup>();
  const { filter: groupAdminFilter } = useStoreSelector((store) => store.groupAdmin);

  const { filter, list } = useStoreSelector((store) => store.group);
  const { page, rowsPerPage } = filter;
  const { items } = list;

  const handleOpenModal = (group: IGroup) => {
    setSelectedGroup(group);
    setOpenModal(true);
  };

  const fetchGroupList = useCallback(async () => {
    try {
      const response = await getGroupList(filter);

      setGroupList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de grupos', error);
    }
  }, [page, rowsPerPage]);

  const handleUpdateGroup = async (newName: string) => {
    if (!selectedGroup) return;

    const normalizedName = newName.trim().toLowerCase();

    const isDuplicate = list.items?.some((group) => group.name.trim().toLowerCase() === normalizedName);

    if (isDuplicate) {
      showSnackbar('Já existe um grupo com este nome', 'error', true);
      return;
    }

    try {
      const response = await updateGroupsName({
        id: selectedGroup.id,
        name: newName,
      });

      setGroupName(response.data);
      showSnackbar('Grupo editado com sucesso', 'success', true);
      setOpenModal(false);
      fetchGroupList();
      getGroupAdminList();
      getAllGroups();
    } catch (error) {
      showErrorDialog('Erro ao editar grupo', error);
    }
  };

  const getGroupAdminList = async () => {
    try {
      const response = await api.getGroupAdminList(groupAdminFilter);
      setGroupAdminList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de grupo admins', error);
    }
  };

  const getAllGroups = async () => {
    try {
      const response = await api.getAllStoreGroups();
      setAllGroups(response.data);
    } catch (error) {
      showErrorDialog('Erro ao buscar todos os grupos', error);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGroupList();
  }, [fetchGroupList]);

  if (!Array.isArray(items) || items.length === 0) {
    return <NoItemsFound />;
  }

  return (
    <BoxCustom>
      {list.items.map((group) => (
        <Container key={group.id}>
          <Content>
            <SubtitleMediumBoldStyled>Nome</SubtitleMediumBoldStyled>
            <SubtitleMediumStyled>{group.name}</SubtitleMediumStyled>
          </Content>
          <AlignStatusContainer>
            <Content>
              <SubtitleMediumBoldStyled>Ações</SubtitleMediumBoldStyled>
              <SubtitleMediumStyled>Editar</SubtitleMediumStyled>
            </Content>
            <Content>
              <EditIcon onClick={() => handleOpenModal(group)} />
            </Content>
          </AlignStatusContainer>
        </Container>
      ))}
      <GenericNameModal
        title="Editar grupo"
        active={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleUpdateGroup}
        currentName={selectedGroup?.name}
      />
    </BoxCustom>
  );
};
